import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzUploadFile, NzUploadModule, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { Subscription } from 'rxjs';
import { ModelState } from '../../../store/model/model.state';
import { InspectionModel, UserRole } from '../../../model';
import { AuthenticationState } from '../../../store/auth/auth.state';
import { JsonObjectPreviewComponent } from '../../components/json-object-preview/json-object-preview.component';
import { InspectionModelEditComponent } from './inspection-model-edit.component';

/**
 * What went wrong, from the error the HTTP interceptor rethrows (RFC7807 problem details). The upload's own reasons
 * (400/409) are in `detail`; for a server exception (5xx) `detail` is the stack trace, so `title` (the exception
 * message) is used; without a body only `title` is set ("Http failure response ...").
 */
export function uploadErrorMessage(err: unknown): string {
  const problem = (err ?? {}) as { status?: unknown; detail?: unknown; title?: unknown; message?: unknown };
  const isServerException = typeof problem.status === 'number' && problem.status >= 500;
  const candidates = isServerException ? [problem.title, problem.message] : [problem.detail, problem.title, problem.message];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  }
  return $localize`:@@modelUploadUnknownError:Unknown error`;
}

@Component({
  selector: 'app-models',
  imports: [
    NzPageHeaderModule,
    NzSpaceModule,
    NzUploadModule,
    NzButtonModule,
    NzIconModule,
    NzTableModule,
    JsonObjectPreviewComponent,
    InspectionModelEditComponent,
  ],
  templateUrl: './models.component.html',
  styleUrls: ['./models.component.scss'],
})
export class ModelsComponent implements OnInit {
  readonly state = inject(ModelState);
  readonly authState = inject(AuthenticationState);
  private readonly modal = inject(NzModalService);
  private readonly message = inject(NzMessageService);

  editingModel = signal<InspectionModel | null>(null);
  previewModel = signal<InspectionModel | null>(null);
  /** nz-upload's file list under the Upload button; emptied after a successful upload. */
  readonly uploadedFiles = signal<NzUploadFile[]>([]);

  readonly isAdmin = computed(() => this.authState.hasRole(UserRole.Admin));
  readonly canViewJson = computed(() => this.authState.hasAnyRole(UserRole.Admin, UserRole.Laboratory));
  /** Editing inspection-model thresholds is restricted to Laboratory and Admin. */
  readonly canEditModel = computed(() => this.authState.hasAnyRole(UserRole.Laboratory, UserRole.Admin));
  readonly previewModelTitle = computed(() => {
    const m = this.previewModel();
    return m ? `Model JSON — ${m.version}` : null;
  });

  /**
   * nz-upload's request: uploads through the state so the new (inactive) model joins the list, and a failure is shown
   * in a popup. An arrow function because nz-upload calls it unbound.
   */
  readonly uploadModel = (item: NzUploadXHRArgs): Subscription => {
    // postFile is the File the user picked (nz-upload has no nzTransformFile here).
    const file = item.postFile as File;
    return this.state.importModel(file).subscribe({
      next: model => {
        item.onSuccess?.(model, item.file, null);
        this.message.success($localize`:@@modelUploadedInactive:Model ${model.version}:version: uploaded. It is not active: use Edit to activate it.`, { nzDuration: 6000 });
        // Refresh the page, keeping the message: the models again from the server, and no file left under the button.
        this.uploadedFiles.set([]);
        this.state.getAll();
      },
      error: (err: unknown) => {
        item.onError?.(err, item.file);
        this.modal.error({
          nzTitle: $localize`:@@modelUploadFailedTitle:Model upload failed: ${file.name}:fileName:`,
          nzContent: uploadErrorMessage(err),
        });
      },
    });
  };

  ngOnInit(): void {
    this.state.getAll();
  }

  openJsonPreview(model: InspectionModel): void {
    this.previewModel.set(model);
  }

  openEdit(model: InspectionModel): void {
    this.editingModel.set(model);
  }

  onSaved(): void {
    this.editingModel.set(null);
  }

  onCancelled(): void {
    this.editingModel.set(null);
  }

  protected readonly window = window;
}
